import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ApiObjectResponse,
  ApiResponse,
} from '../../../../common/models/api-response.model';
import {
  Department,
  DepartmentDetails,
  DepartmentPosition,
  DepartmentTreeItem,
  DepartmentUserRow,
} from './department-model';
import { ENDPOINTS } from '../../../../common/constants/endpoints.constants';
import { Status } from '../../../../common/enums/user-status';

@Injectable({
  providedIn: 'root',
})
export class DepartmentService {
  http = inject(HttpClient);

  readonly DEPARTMENT_ID = '{departmentId}' as const;

  getDepartmentList(
    page: number,
    per_page: number,
    sort?: { key: string; value: 'asc' | 'desc' },
    filter?: string
  ) {
    let params = new HttpParams()
      .append('page', page)
      .append('per_page', per_page);

    if (sort?.key) {
      params = params.append(`sort[${sort.key}]`, sort.value);
    }
    if (filter && filter !== 'all') {
      params = params.append('filters[state]', filter);
    }
    return this.http.get<ApiResponse<Department>>(
      ENDPOINTS.Dashboard.Departments.List,
      {
        params,
      }
    );
  }

  getDepartment(id: string) {
    return this.http.get<{ data: DepartmentDetails }>(
      ENDPOINTS.Dashboard.Departments.Details.replace(this.DEPARTMENT_ID, id)
    );
  }

  getLegalEntities() {
    const url = ENDPOINTS.Lookup.LegalEntities;
    return this.http.get<{ data: any }>(url);
  }

  getDirectManager() {
    const url = ENDPOINTS.Lookup.User;
    const params = new HttpParams().append(
      'filters[not_states][0]',
      Status.DELETED
    );
    return this.http.get<{ data: any }>(url, {
      params,
    });
  }

  getDepartments(legal_entity_id: number) {
    const url = ENDPOINTS.Lookup.Departments;
    const params = new HttpParams().append(
      'filters[legal_entity_id]',
      legal_entity_id
    );
    return this.http.get<{ data: any }>(url, {
      params,
    });
  }

  addDepartment(data: any) {
    return this.http.post(ENDPOINTS.Dashboard.Departments.Create, data);
  }

  updateDepartment(data: any, id: string) {
    return this.http.put(
      ENDPOINTS.Dashboard.Departments.Update.replace(this.DEPARTMENT_ID, id),
      data
    );
  }

  activateDepartment(departmentId: string) {
    return this.http.put(
      ENDPOINTS.Dashboard.Departments.Activate.replace(
        this.DEPARTMENT_ID,
        departmentId
      ),
      {}
    );
  }

  deactivateDepartment(departmentId: string) {
    return this.http.put(
      ENDPOINTS.Dashboard.Departments.Deactivate.replace(
        this.DEPARTMENT_ID,
        departmentId
      ),
      {}
    );
  }

  deleteDepartment(departmentId: string) {
    return this.http.delete(
      ENDPOINTS.Dashboard.Departments.Delete.replace(
        this.DEPARTMENT_ID,
        departmentId
      )
    );
  }

  getDepartmentUsers(
    departmentId: string,
    page: number,
    name: string,
    perPage?: number
  ) {
    let params = new HttpParams()
      .append('page', page)
      .append('filters[search]', name);
    if (perPage) {
      params = params.append('per_page', perPage);
    }
    return this.http.get<ApiResponse<DepartmentUserRow>>(
      ENDPOINTS.Dashboard.Departments.Users.replace(
        this.DEPARTMENT_ID,
        departmentId
      ),
      { params }
    );
  }

  getDepartmentTree() {
    return this.http.get<ApiObjectResponse<DepartmentTreeItem[]>>(
      ENDPOINTS.Dashboard.Departments.Tree
    );
  }

  getDepartmentPositions(departmentId: string, page: number, perPage: number) {
    const params = new HttpParams()
      .append('page', page)
      .append('per_page', perPage)
      .append('filters[department]', departmentId);
    return this.http.get<ApiResponse<DepartmentPosition>>(
      ENDPOINTS.Dashboard.Positions.List,
      { params }
    );
  }
}
